import { Button } from '@yellowcard/components-library-vue3';
import { defineComponent } from 'vue';
import { storeToRefs } from 'pinia';
import useTransactionsStore from '@/store/modules/transactions';
import { usePaymentStore } from '@/store/modules/payment';
import { PaymentTypes } from '@/types/payment';
import { createWalletClient, custom, encodeFunctionData, parseUnits, getContract, } from 'viem';
import { stableTokenABI } from '@celo/abis';
import { celo, celoAlfajores } from 'viem/chains';
import { numberFormat } from '@/utils/helpers';
import { TransactTypes } from '@/types/transact';
import abi from '../../abi/cusd-contract.json';
import LoaderComponent from '../general/LoaderComponent.vue';
export default defineComponent({
    name: 'AuthoriseMinipay',
    props: {},
    components: {
        Button,
        LoaderComponent,
    },
    setup() {
        const paymentsStore = usePaymentStore();
        const { currentStep: paymentCurrentStep } = storeToRefs(paymentsStore);
        const transactionStore = useTransactionsStore();
        const { selectedCrypto, selectedCryptoAmount, selectedCurrency, selectedCurrencyAmount, payment, loading, minipayError, } = storeToRefs(transactionStore);
        function getAmountToSend() {
            let decimals;
            switch (payment.value?.settlementInfo.cryptoCurrency) {
                case TransactTypes.Tokens.USDC:
                case TransactTypes.Tokens.USDT:
                    decimals = 6;
                    break;
                case TransactTypes.Tokens.CUSD:
                    decimals = 18;
                    break;
                default:
                    throw new Error('invalid minipay token');
            }
            return parseUnits(`${payment.value?.settlementInfo.cryptoAmount}`, decimals);
        }
        const isTestEnv = ['sandbox', 'staging'].includes(process.env.VUE_APP_ENVIRONMENT);
        function getTokenContractAddress() {
            // https://docs.celo.org/token-addresses
            const CUSD_CELO_TOKEN_ADDRESS_MAINNET = '0x765DE816845861e75A25fCA122bb6898B8B1282a';
            const CUSD_CELO_TOKEN_ADDRESS_ALFAJORES_TESTNET = '0x874069fa1eb16d44d622f2e0ca25eea172369bc1';
            // https://tether.to/en/supported-protocols
            // https://celoscan.io/token/0x48065fbbe25f71c9282ddf5e1cd6d6a887483d5e
            const USDT_CELO_TOKEN_ADDRESS_MAINNET = '0x48065fbBE25f71C9282ddf5e1cD6D6A887483D5e';
            // https://alfajores.celoscan.io/token/0x9Ae90C20aFCEDA659bDbC5F77A09f1A5771F140f
            const USDT_CELO_TOKEN_ADDRESS_ALFAJORES_TESTNET = '0x9Ae90C20aFCEDA659bDbC5F77A09f1A5771F140f';
            // https://celoscan.io/token/0xef4229c8c3250c675f21bcefa42f58efbff6002a
            const USDC_CELO_TOKEN_ADDRESS_MAINNET = '0xef4229c8c3250C675F21BCefa42f58EfbfF6002a';
            // https://alfajores.celoscan.io/token/0x2F25deB3848C207fc8E0c34035B3Ba7fC157602B
            const USDC_CELO_TOKEN_ADDRESS_ALFAJORES_TESTNET = '0x2F25deB3848C207fc8E0c34035B3Ba7fC157602B';
            const CONTRACTS = {
                CUSD: {
                    mainnet: CUSD_CELO_TOKEN_ADDRESS_MAINNET,
                    testnet: CUSD_CELO_TOKEN_ADDRESS_ALFAJORES_TESTNET,
                },
                USDC: {
                    mainnet: USDC_CELO_TOKEN_ADDRESS_MAINNET,
                    testnet: USDC_CELO_TOKEN_ADDRESS_ALFAJORES_TESTNET,
                },
                USDT: {
                    mainnet: USDT_CELO_TOKEN_ADDRESS_MAINNET,
                    testnet: USDT_CELO_TOKEN_ADDRESS_ALFAJORES_TESTNET,
                },
            };
            const token = payment.value?.settlementInfo.cryptoCurrency;
            switch (token) {
                case TransactTypes.Tokens.CUSD:
                case TransactTypes.Tokens.USDC:
                case TransactTypes.Tokens.USDT:
                    return isTestEnv
                        ? CONTRACTS[token].testnet
                        : CONTRACTS[token].mainnet;
                default:
                    throw new Error('invalid minipay token');
            }
        }
        async function checkBalance(client, address, amount) {
            const StableTokenContract = getContract({
                abi: stableTokenABI,
                address: getTokenContractAddress(),
                client,
            });
            const balance = await StableTokenContract.read.balanceOf([address]);
            console.error(`Minipay wallet balance: ${balance} --- Amount to withdraw: ${amount}`);
            if (balance < amount) {
                throw new Error('INSUFFICIENT_MINIPAY_BALANCE');
            }
        }
        async function minipayTransfer() {
            const chain = isTestEnv ? celoAlfajores : celo;
            const walletClient = createWalletClient({
                chain,
                transport: custom(window.ethereum),
            });
            const amount = getAmountToSend();
            const [account] = await walletClient.requestAddresses();
            await checkBalance(walletClient, account, amount);
            return walletClient.sendTransaction({
                to: getTokenContractAddress(),
                data: encodeFunctionData({
                    abi,
                    functionName: 'transfer',
                    args: [
                        payment.value?.settlementInfo.walletAddress,
                        amount,
                    ],
                }),
                chain,
                account,
            });
        }
        const authoriseMinipayAccess = async () => {
            loading.value = true;
            try {
                await minipayTransfer();
                loading.value = false;
                paymentCurrentStep.value = PaymentTypes.SellPaymentSteps.PAYMENT_4;
            }
            catch (e) {
                loading.value = false;
                minipayError.value =
                    e.message || 'An unexpected error occurred. Try again later.';
                console.error('[MINIPAY ERROR]', e);
                paymentCurrentStep.value = PaymentTypes.SellPaymentSteps.PAYMENT_9;
                transactionStore.cancelPayment();
            }
        };
        const denyMinipayAccess = () => {
            paymentCurrentStep.value = PaymentTypes.SellPaymentSteps.PAYMENT_6;
        };
        return {
            authoriseMinipayAccess,
            denyMinipayAccess,
            selectedCrypto,
            selectedCryptoAmount,
            selectedCurrency,
            selectedCurrencyAmount,
            numberFormat,
            loading,
        };
    },
});
